import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import { Link, useNavigate } from 'react-router-dom';
import LoadingModal from './components/modals/LoadingModal';
import Pagination10 from './components/Pagination10';
import PageFooter from './components/PageFooter';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import ConfirmModal from './components/modals/ConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import usePagination10 from './components/hooks/usePagination10';
import Layout from './components/Layout';
import TableContainer from './components/TableContainer';
import TableHeader from './components/TableHeader';
import ClienteModal from './components/modalServices/ClienteModal';
import Toolbar from './components/Toolbar';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import ResponsiveShadow from './components/ResponsiveShadow';

function Clientes() {
  const [Clientes, setClientes] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [atualizar, setAtualizar] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const token = localStorage.getItem('token');
  const [isLoading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();

  // Função para gerenciar seleção/desseleção de cliente
  const handleRowClick = cliente => {
    // Se clicar no mesmo cliente já selecionado, desmarca
    if (selectedCliente && selectedCliente.cnpj === cliente.cnpj) {
      setSelectedCliente(null);
    } else {
      setSelectedCliente(cliente);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/');
    }
    setLoading(true);
    axios
      .get(`${API_BASE_URL.CLIENTE}/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(result => {
        setClientes(result.data);
      })
      .catch(error => {
        console.error('Erro ao obter clientes:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [atualizar]);

  // Função para lidar com a exclusão
  function handleDelete(c) {
    showConfirmModal('delete', () => handleConfirmDelete(c));
  }

  function handleConfirmDelete(c) {
    axios
      .get(`${API_BASE_URL.USUARIO_BIND}/exists_cnpj/` + c.cnpj, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(result => {
        if (result.data.exists) {
          showDefaultModal(
            'error',
            'Este Cliente/Entidade possui vínculos e não pode ser excluído. Exclua os vínculos antes de tentar novamente.'
          );
        } else {
          axios
            .delete(`${API_BASE_URL.CLIENTE}/delete/` + c.cnpj, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            })
            .then(() => {
              setAtualizar(prev => !prev);
            });
        }
      })
      .catch(error => {
        console.error('Erro ao verificar vínculos:', error);
        showDefaultModal('error', 'Erro ao excluir o registro!');
        setTimeout(() => navigate('/'), 0);
      });
  }

  function handleOpenModal(cliente = null) {
    setEditingCliente(cliente);
    setIsModalOpen(true);
  }

  function handleCloseModal() {
    setIsModalOpen(false);
    setEditingCliente(null);
  }

  function handleModalSuccess() {
    setAtualizar(prev => !prev);
    showDefaultModal('success', 'Cliente salvo com sucesso!');
  }

  // Função para filtrar os fabricantes com base na busca
  const normalizeString = str => {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase();
  };

  // Filtra os fabricantes com base no termo de busca
  const clientesFiltrados = Clientes.filter(c => {
    const normalizedBusca = normalizeString(filtro);
    return (
      normalizeString(c.nome).includes(normalizedBusca) ||
      normalizeString(c.cnpj).includes(normalizedBusca) ||
      normalizeString(c.logrCidade).includes(normalizedBusca)
    );
  });

  // Paginação dos clientes filtrados
  const totalItems = clientesFiltrados.length;
  const { setPagination } = usePagination10(totalItems);
  const selectedClientes = clientesFiltrados.slice(
    setPagination.startIndex,
    setPagination.startIndex + setPagination.rowsPerPage
  );

  return (
    <Layout>
      <ResponsiveShadow>
        <PageHeader title="Entidades Cadastradas" icon="bi-building" closeRoute="/home" backButton={false} />
        <PageContent style={{ paddingBottom: 0 }}>
          <Toolbar
            selectedId={selectedCliente ? selectedCliente.cnpj : null}
            items={Clientes}
            onCreate={() => handleOpenModal()}
            onEdit={(id) => { if (id) handleOpenModal(Clientes.find(c => c.cnpj === id)); }}
            onDelete={(id) => { if (id) handleDelete(Clientes.find(c => c.cnpj === id)); }}
            searchValue={filtro}
            onSearch={(v) => setFiltro((v||'').toUpperCase())}
            searchWidth="240px"
          />
            {/* Conteúdo principal: loading + tabela */}
            <LoadingModal loading={isLoading} />

            <TableContainer className="table-responsive mb-2">
              <table className="table table-striped table-hover table-sm small table-custom-radius">
                <TableHeader columns={[
                  { label: 'CNPJ' },
                  { label: 'Nome' },
                  { label: 'Cidade' },
                  { label: 'UF' },
                ]} />
                <tbody>
                  {!isLoading &&
                    selectedClientes.map(c => {
                      const isSelected =
                        selectedCliente && selectedCliente.cnpj === c.cnpj;
                      return (
                        <tr
                          key={c.cnpj}
                          onClick={() => handleRowClick(c)}
                          style={{ cursor: 'pointer' }}
                          className={isSelected ? 'table-active' : ''}
                          title={
                            isSelected
                              ? 'Clique novamente para desmarcar'
                              : 'Clique para selecionar'
                          }
                        >
                          <td className={isSelected ? 'fw-bold text-primary' : ''}>
                            {c.cnpj}
                          </td>
                          <td className={isSelected ? 'fw-bold text-primary' : ''}>
                            {c.nome}
                          </td>
                          <td className={isSelected ? 'fw-bold text-primary' : ''}>
                            {c.logrCidade}
                          </td>
                          <td className={isSelected ? 'fw-bold text-primary' : ''}>
                            {c.logrUf}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </TableContainer>

          </PageContent>

          <PageFooter>
            <Pagination10 {...setPagination} />
          </PageFooter>

        </ResponsiveShadow>

        <ClienteModal
        isOpen={isModalOpen}
        cliente={editingCliente}
        onClose={handleCloseModal}
        onSuccess={handleModalSuccess}
      />

      <ConfirmModal {...setConfirmModal} />
      <DefaultModal {...setDefaultModal} />
    </Layout>
  );
}

export default Clientes;
